import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import { Camera, Search, X, Barcode, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BarcodeScanner = ({ isOpen, onClose, onProductFound }) => {
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);
    } catch (err) {
      setCameraError("Unable to access camera. Please use manual entry.");
      toast.error("Camera access denied");
    }
  };

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }
  }, [isOpen, stopCamera]);

  const searchProduct = async (code) => {
    if (!code.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/lookup-barcode/${code}`);
      onProductFound(response.data);
      toast.success("Product found!");
      onClose();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Product not found");
      } else {
        toast.error("Search failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    searchProduct(manualCode);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { stopCamera(); onClose(); }}}>
      <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Barcode className="w-5 h-5" />
            Barcode Scanner
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera Scanner */}
          {scanning ? (
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full rounded-lg bg-black"
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-32 border-2 border-[#d4af37] rounded-lg opacity-50" />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={stopCamera}
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70"
              >
                <X className="w-4 h-4" />
              </Button>
              <p className="text-center text-xs text-slate-400 mt-2">
                Position barcode within the frame
              </p>
            </div>
          ) : (
            <Card className="bg-slate-700 border-slate-600">
              <CardContent className="p-6 text-center">
                {cameraError ? (
                  <p className="text-yellow-400 text-sm mb-4">{cameraError}</p>
                ) : (
                  <Button 
                    onClick={startCamera}
                    className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Camera Scanner
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Manual Entry */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-600" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-slate-800 px-2 text-slate-400">Or enter manually</span>
            </div>
          </div>

          <form onSubmit={handleManualSearch} className="flex gap-2">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Enter barcode or SKU..."
              className="bg-slate-700 border-slate-600"
            />
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>

          <p className="text-xs text-slate-400 text-center">
            Scan a product barcode or enter the SKU/barcode number manually
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
